import type { ReactNode } from "react";

/**
 * Numbered heading of one prescription request section (reason, request,
 * urgency) with an 18px hint.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <PrescriptionSectionHeading number={1} title={t("…reason-title")} hint={t("…reason-hint")} />
 * ```
 */
export function PrescriptionSectionHeading({
  number,
  title,
  hint,
  id,
}: {
  readonly number: number;
  readonly title: ReactNode;
  readonly hint: ReactNode;
  /** Id of the heading, for `aria-labelledby` of the section's choices. */
  readonly id?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        aria-hidden
        className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-subtle text-lg font-semibold text-primary-ink"
      >
        {number}
      </span>
      <div className="min-w-0">
        <h2 id={id} className="m-0 text-xl font-semibold">
          {title}
        </h2>
        <p className="m-0 text-lg text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}
